/**
 * Shared mobile purchase dock (F3) -- Matching + Fabric.
 *
 * This file is a presentation-only mirror + click-forwarder. It does
 * NOT contain, and must never gain:
 *   - cart logic / a second /cart/add.js request
 *   - validation logic
 *   - pricing or discount calculation
 *   - line-item construction
 *   - customisation logic
 *   - ProductEngaged / ProductConfigured logic
 *   - AddToCart tracking
 *   - cart drawer logic
 *   - a builder state machine
 *
 * It only ever reads three things that the real builder
 * (assets/matching-set-builder.js or assets/fabric-product-
 * options.js) already computes and writes to the DOM on its own:
 *   - the real CTA's `disabled` property        (readiness)
 *   - the real CTA's `aria-busy` attribute       (loading state)
 *   - the real final-price node's `textContent`  (You Pay)
 * ...and it forwards a tap on the dock button to a real `.click()`
 * on the real CTA, so the existing click-delegation listener and
 * handleAddToCart() run exactly as if the customer had tapped the
 * real button. There is exactly one canonical Add to Cart path per
 * builder; this file never calls handleAddToCart() or any other
 * builder-internal function directly.
 *
 * Display rule (kept deliberately simple, per spec):
 *   show the dock only when BOTH are true:
 *     1. the real CTA is enabled (disabled === false)
 *     2. the real CTA is not currently visible in the viewport
 *   Everything else (incomplete builder, native CTA visible, or the
 *   native CTA re-disabling itself) hides the dock immediately. No
 *   scroll-direction logic, no "first engagement" gate, no
 *   contextual configuration messaging.
 */
(function () {
  'use strict';

  var MOBILE_QUERY = '(max-width: 767px)';

  function initDock(root) {
    var builder = root.getAttribute('data-purchase-dock-builder');
    if (!builder) return;

    var nativeCta = document.querySelector('[data-' + builder + '-add-to-cart]');
    var priceNode = document.querySelector('[data-' + builder + '-final-price]');

    // Required elements missing (e.g. Fabric's own no-catalog-data
    // fallback branch, which renders no CTA/price at all) -- stay
    // completely inert rather than guessing at partial behaviour.
    if (!nativeCta || !priceNode) return;

    var dockPrice = root.querySelector('[data-purchase-dock-price]');
    var dockCta = root.querySelector('[data-purchase-dock-cta]');
    if (!dockPrice || !dockCta) return;

    var mobileQuery = (typeof window.matchMedia === 'function') ? window.matchMedia(MOBILE_QUERY) : null;
    // Assume the real CTA is visible until the IntersectionObserver's
    // first callback proves otherwise -- the safe default is "dock
    // hidden", never a flash of the dock before real state is known.
    var nativeCtaVisible = true;

    function isMobile() {
      return !mobileQuery || mobileQuery.matches;
    }

    function setDockHeightVar(visible) {
      // Bakes the "small visual gap" the WhatsApp button should keep
      // above the dock into the variable itself, so
      // sections/whatsapp-button.liquid only ever needs to add this
      // one variable to its own `bottom` -- 0px whenever the dock is
      // hidden or we are not on a mobile viewport.
      var px = 0;
      if (visible) {
        px = root.getBoundingClientRect().height + 12;
      }
      document.documentElement.style.setProperty('--pt-purchase-dock-height', px + 'px');
    }

    function syncPriceText() {
      var text = priceNode.textContent;
      if (dockPrice.textContent !== text) dockPrice.textContent = text;
    }

    function syncCtaState() {
      var busy = nativeCta.getAttribute('aria-busy') === 'true';
      if (busy) {
        dockCta.disabled = true;
        dockCta.setAttribute('aria-busy', 'true');
        dockCta.textContent = 'Adding…';
      } else {
        dockCta.removeAttribute('aria-busy');
        dockCta.disabled = !!nativeCta.disabled;
        dockCta.textContent = 'Add to Cart';
      }
    }

    function updateVisibility() {
      var ready = !nativeCta.disabled;
      var shouldShow = isMobile() && ready && !nativeCtaVisible;
      root.classList.toggle('purchase-dock--visible', shouldShow);
      setDockHeightVar(shouldShow);
    }

    // Native CTA visibility -- the sole "is the real button on
    // screen" signal, per spec (no scroll listeners).
    var ctaVisibilityObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        nativeCtaVisible = entry.isIntersecting;
      });
      updateVisibility();
    }, { threshold: 0 });
    ctaVisibilityObserver.observe(nativeCta);

    // Mirror the real CTA's disabled/aria-busy attributes only --
    // never re-derive readiness from anything else.
    var ctaStateObserver = new MutationObserver(function () {
      syncCtaState();
      updateVisibility();
    });
    ctaStateObserver.observe(nativeCta, { attributes: true, attributeFilter: ['disabled', 'aria-busy'] });

    // Mirror the real final-price node's rendered text only -- no
    // currency/discount maths of any kind here.
    var priceObserver = new MutationObserver(syncPriceText);
    priceObserver.observe(priceNode, { characterData: true, childList: true, subtree: true });

    if (mobileQuery && typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', updateVisibility);
    }
    window.addEventListener('resize', function () {
      if (root.classList.contains('purchase-dock--visible')) setDockHeightVar(true);
    });

    // Click forwarding only -- never handleAddToCart() or any other
    // builder-internal function. This is a real .click() on the real
    // button, so the existing click-delegation listener there is
    // what actually runs the existing, unmodified purchase logic.
    dockCta.addEventListener('click', function () {
      if (dockCta.disabled) return;
      nativeCta.click();
    });

    syncPriceText();
    syncCtaState();
    updateVisibility();
  }

  function init() {
    var docks = document.querySelectorAll('[data-purchase-dock]');
    for (var i = 0; i < docks.length; i++) initDock(docks[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
